import { inject, injectable, interfaces } from 'inversify';
import { action, IReactionDisposer, makeObservable, reaction } from 'mobx';
import SimpleCrypto from 'simple-crypto-js';
import storage from 'store';
import { IAnnotationTypeStore } from '../modules/annotation/submodules/annotationTypes/annotationType.store';
import { IClarificationsStore } from '../modules/annotation/submodules/clarifications/Clarifications.store';
import { IAuthStore } from '../modules/auth/auth.store';
import { IProjectsStore } from '../modules/projects/projects.store';
import { IUserStore } from '../modules/user/user.store';
import { IWorkspacesStore } from '../modules/workspaces/workspaces.store';
import { ICryptoStore } from '../stores/crypto.store';
import { ICryptoService } from './crypto.service';

interface IWorkspaceCryptoEntry {
  workspaceId: string;
  checksum: string;
  rawKey: string;
}

export interface IStorageService {
  initializeLocalStorage(): void;
  initializeCryptoLocalStorage(): Promise<void>;
}

export namespace IStorageService {
  export const $: interfaces.ServiceIdentifier<IStorageService> = Symbol('STORAGE_SERVICE');
}

@injectable()
export class StorageService implements IStorageService {
  constructor(
    @inject(IProjectsStore.$) private projectsStore: IProjectsStore,
    @inject(IWorkspacesStore.$) private workspaceStore: IWorkspacesStore,
    @inject(IUserStore.$) private userStore: IUserStore,
    @inject(IAnnotationTypeStore.$) private annotationTypeStore: IAnnotationTypeStore,
    @inject(IAuthStore.$) private authStore: IAuthStore,
    @inject(IClarificationsStore.$) private clarificationsStore: IClarificationsStore,
    @inject(ICryptoService.$) private cryptoService: ICryptoService,
    @inject(ICryptoStore.$) private cryptoStore: ICryptoStore
  ) {
    makeObservable(this);
  }

  reactions: IReactionDisposer[] = [];
  simpleCrypto!: SimpleCrypto;

  @action.bound
  initializeLocalStorage(): void {
    this.reactions.forEach((reaction) => {
      reaction();
    });
    this.reactions = [];

    this.trackProperty(this.authStore, 'versionChanged');
    this.trackProperty(this.workspaceStore, 'preferredWorkspaceId');
    this.trackProperty(this.userStore, 'opacityLevel');
    this.trackProperty(this.annotationTypeStore, 'hiddenAnnotationTypes');
    this.trackProperty(this.clarificationsStore, 'clarificationsListSortDirection');
    this.trackProperty(this.userStore, 'isImprovedVisibilityCursorEnabled');
    this.trackProperty(this.userStore, 'areCursorGuidesEnabled');

    this.trackAuthToken();
    this.trackProjectsOrder();
    this.trackSelectedAttributesPerProject();
  }

  @action.bound
  async initializeCryptoLocalStorage(): Promise<void> {
    this.simpleCrypto = new SimpleCrypto(this.userStore.userInfo.id);

    const storageKey = 'workspaceCryptos';

    const data = window.localStorage.getItem(storageKey);

    if (data) {
      try {
        const decrypted = this.simpleCrypto.decrypt(data) as IWorkspaceCryptoEntry[];
        const promises = decrypted.map(async (x) => {
          await this.cryptoService.loadKey(x.workspaceId, x.rawKey, x.workspaceId, x.checksum);
        });
        await Promise.allSettled(promises);
      } catch {
        window.localStorage.setItem(storageKey, JSON.stringify([]));
      }
    }

    this.reactions.push(
      reaction(
        () => this.cryptoStore.workspaceCryptos,
        (value) => {
          const mapped = value.map((x) => ({ workspaceId: x.workspaceId, rawKey: x.rawKey, checksum: x.checksum }));
          const encrypted = this.simpleCrypto.encrypt(mapped);
          window.localStorage.setItem('workspaceCryptos', encrypted);
        }
      )
    );

    window.addEventListener('storage', (evt: StorageEvent) => {
      if (evt.key !== 'workspaceCryptos' || !evt.newValue) {
        return;
      }

      const list = this.simpleCrypto.decrypt(evt.newValue) as IWorkspaceCryptoEntry[];

      const promises = list.map(async (x) => {
        if (!this.cryptoService.hasKey(x.workspaceId)) {
          await this.cryptoService.loadKey(x.workspaceId, x.rawKey, x.workspaceId, x.checksum);
        }
      });

      void Promise.allSettled(promises);
    });
  }

  trackProperty<T>(store: T, key: keyof T): void {
    const value = storage.get(key.toString());
    store[key] = value ? value : store[key];
    this.reactions.push(
      reaction(
        () => store[key],
        (value) => {
          storage.set(key.toString(), value);
        }
      )
    );
  }

  trackProjectsOrder(): void {
    this.projectsStore.projectsPaging = { ...this.projectsStore.projectsPaging, ...storage.get('prefferedProjectsOrder') };
    this.reactions.push(
      reaction(
        () => this.projectsStore.projectsPaging,
        (value) => {
          storage.set('prefferedProjectsOrder', { orderBy: value.orderBy, orderType: value.orderType });
        }
      )
    );
  }

  trackSelectedAttributesPerProject(): void {
    const existingEntry = storage.get('selectedAttributesPerProject');
    if (existingEntry) {
      this.userStore.selectedAttributesPerProject = new Map(Object.entries(JSON.parse(existingEntry)));
    }
    this.reactions.push(
      reaction(
        () => this.userStore.selectedAttributesPerProjectSerialized,
        (value) => {
          storage.set('selectedAttributesPerProject', value);
        }
      )
    );
  }

  trackAttributesNamesVisibilityPerProject(): void {
    const existingEntry = storage.get('attributesNameVisibilityPerProject');
    if (existingEntry) {
      this.userStore.attributesNamesVisibilityPerProject = new Map(Object.entries(JSON.parse(existingEntry)));
    }
    this.reactions.push(
      reaction(
        () => this.userStore.attributesNamesVisibilityPerProject,
        (value) => {
          storage.set('attributesNameVisibilityPerProject', value);
        }
      )
    );
  }

  trackAuthToken(): void {
    this.trackProperty(this.authStore, 'token');

    window.addEventListener('storage', (evt: StorageEvent) => {
      if (evt.key !== 'token') {
        return;
      }
      const isAuthenticated = this.authStore.isAuthenticated;
      this.authStore.updateToken(evt.newValue ? JSON.parse(evt.newValue) : '');
      if (!isAuthenticated) {
        window.location.reload();
      }
    });
  }
}
